import { action, computed, makeObservable, observable } from 'mobx';
import { Auth, getRegisteredClass } from 'sat-core';

const PROGRESS_ENDPOINTS = {
  APPROVE_ACTIVITY: '/api/approveActivity',
  CLEAR_TO_LPL: '/api/clearLPLAdminSelection',
  DISAPPROVE_ACTIVITY: '/api/dontApproveActivity',
  GET_BENCHMARK_PROGRESS: '/api/getBenchmarkProgress',
  GET_CLASSROOM_PROGRESS: '/api/getClassroomProgress',
  GET_LEADERBOARD: '/api/viewReportOutput',
  SEND_TO_LPL: '/api/sendActivityInstanceToLPLAdmin',
  RE_OPEN_ACTIVITY: '/api/reopenStudentInstance'
};

export const TROPHY = {
  GOLD: 'GOLD',
  SILVER: 'SILVER',
  BRONZE: 'BRONZE',
  NONE: 'NONE'
};

export class ProgressManager {
  @observable benchmarkProgress = new Map();

  @observable classroomProgress = [];

  @observable leaderboardJson = {};

  @observable approvalStatusMessage = '';

  constructor() {
    makeObservable(this);
  }

  @action
  clearAll = () => {
    this.benchmarkProgress.clear();
    this.classroomProgress = [];
    this.leaderboardJson = {};
    this.approvalStatusMessage = '';
  }

  @action setBenchmarkProgress = (bmProgress) => {
    this.benchmarkProgress.set(bmProgress.courseResourceElementId, bmProgress);
  }

  @computed get speedometerObject() {
    const total = this.classroomProgress.length;
    let completed = 0;

    this.classroomProgress.forEach((item) => {
      if (item.trophy !== TROPHY.NONE) {
        ++completed;
      }
    });

    return {
      completed,
      total
    };
  }

  @action setLeaderboardJson = (leaderboardJson) => {
    this.leaderboardJson = leaderboardJson;
  }

  @action setClassroomProgress = (data) => {
    if (data === null || data === undefined) {
      this.classroomProgress.replace([]);
    } else {
      this.classroomProgress.replace(data);
    }
  }

  @action clearApprovalStatusMessage = () => {
    this.approvalStatusMessage = '';
  }

  openActivity = async (activityInstanceId) => {
    try {
      const body = {
        activityInstanceId
      };
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.RE_OPEN_ACTIVITY}?activityInstanceId=${activityInstanceId}`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  fetchClassroomProgress = async (classroomId) => {
    try {
      this.classroomProgress = [];
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.GET_CLASSROOM_PROGRESS}?classroomId=${classroomId}`, {
        method: 'GET'
      });
      if (response && response.status === 'SUCCESS') {
        this.setClassroomProgress(response.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  fetchBenchmarkProgress = async (classroomId, ids) => {
    try {
      this.benchmarkProgress.clear();
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.GET_BENCHMARK_PROGRESS}?classroomId=${classroomId}&courseResourceElementIds=${ids}`, {
        method: 'GET'
      });
      if (response && response.status === 'SUCCESS') {
        this.benchmarkProgress.clear();
        response.data.forEach((bmProgress) => {
          this.setBenchmarkProgress(bmProgress);
        });
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  sendToLPLAdmin = async (activityInstanceId) => {
    const body = {
      activityInstanceId
    };

    try {
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.SEND_TO_LPL}`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  fetchLeaderboard = async (institutionId, courseId) => {
    try {
      this.setLeaderboardJson({});
      const entityId = institutionId + courseId;
      // eslint-disable-next-line max-len
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.GET_LEADERBOARD}?entityId=${entityId}&reportId=EMPOWERED_LEADERBOARD`, {
        method: 'GET'
      });
      if (response && response.status === 'SUCCESS') {
        if (response.data?.length > 0) {
          this.setLeaderboardJson(response.data[0].contentJson);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  @computed get topRankedLeaderboardFaculties() {
    const leaderboard = this.leaderboardJson.classLeaderboard || this.leaderboardJson.leaderboard;
    if (!Array.isArray(leaderboard) || !leaderboard?.length) {
      return [];
    }
    const maxLength = 3;
    return leaderboard.filter((facultyRankData) => !!facultyRankData).slice(0, maxLength);
  }

  @computed get focusedRankedLeaderboardFaculties() {
    const leaderboard = this.leaderboardJson.classLeaderboard || this.leaderboardJson.leaderboard;

    let focusedList = [];

    if (!Array.isArray(leaderboard) || !leaderboard?.length) {
      return focusedList;
    }

    const { institutionId } = this.leaderboardJson;

    const { hasClassroomLeaderboardData } = this;

    const targetIndex = hasClassroomLeaderboardData ? (
      this.topRankedLeaderboardClassroomIndexForCurrentUser
    ) : (
      leaderboard.findIndex((rankData) => {
        return rankData.id === institutionId;
      })
    );

    const maxLength = 3;

    const before = targetIndex - 1;
    let after = targetIndex + 1;

    if (before >= 0 && after < leaderboard.length) {
      focusedList.push(leaderboard[before]);
      focusedList.push(leaderboard[targetIndex]);
      focusedList.push(leaderboard[after]);
    } else if (targetIndex >= 0 && targetIndex <= 2) {
      for (let i = 0; i < leaderboard.length; ++i) {
        focusedList.push(leaderboard[i]);
        if (focusedList.length === maxLength) {
          break;
        }
      }
    } else if (targetIndex <= leaderboard.length - 1 && targetIndex >= leaderboard.length - maxLength) {
      // eslint-disable-next-line for-direction
      for (let i = leaderboard.length - 1; i >= 0; --i) {
        focusedList.push(leaderboard[i]);
        if (focusedList.length === maxLength) {
          break;
        }
      }
      focusedList = focusedList.reverse();
    }

    if (after < leaderboard.length) {
      // noop?
    } else {
      after = targetIndex - 2;
    }

    if (focusedList.length < maxLength && before < leaderboard.length) {
      focusedList.push(leaderboard[before]);
    }

    if (focusedList.length < maxLength && after >= 0) {
      focusedList.push(leaderboard[after]);
    }

    return focusedList.filter((facultyRankData) => !!facultyRankData);
  }

  @computed get topRankedLeaderboardClassroomIndexForCurrentUser() {
    const { hasClassroomLeaderboardData } = this;
    if (!hasClassroomLeaderboardData) {
      return undefined;
    } else {
      const ClassroomService = getRegisteredClass('ClassroomService');
      const currentClassroomId = ClassroomService.getCurrentClassroomId();

      return this.leaderboardJson.classLeaderboard.findIndex((rankData) => {
        return rankData.id === currentClassroomId;
      });
    }
  }

  @computed get hasClassroomLeaderboardData() {
    return !!this.leaderboardJson.classLeaderboard;
  }

  approveActivity = async (activity, note) => {
    const body = {
      activityId: activity.id,
      comments: note
    };

    this.approvalStatusMessage = '';

    try {
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.APPROVE_ACTIVITY}`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      if (response && response.statusMessage === 'notScored') {
        this.approvalStatusMessage = 'Activity not yet scored, or the score is zero. If you wish to approve this activity the score cannot be zero.';
        return false;
      }
      this.approvalStatusMessage = 'There was an error approving this activity.';
      return false;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  disApproveActivity = async (activity, note) => {
    const body = {
      activityId: activity.id,
      comments: note
    };

    try {
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.DISAPPROVE_ACTIVITY}`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  clearToLPLAdmin = async (activityInstanceId) => {
    const body = {
      activityInstanceId
    };

    try {
      const response = await Auth.fetch(`${Auth.ecms}${PROGRESS_ENDPOINTS.CLEAR_TO_LPL}`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}

export default new ProgressManager();
